import React, { useState, useEffect, memo } from 'react';
import ReactDOM from 'react-dom';
import { PRESTATION_TYPES, SESSION_TYPES } from '../../utils/constants';
import { API_ENDPOINTS } from '../../utils/apiConfig';
import SearchableSelect from '../SearchableSelect';
import { hasWeekendBetween } from '../../hooks/useMultiDayData';

const BasicInfoTab = memo(({
  formData,
  handleChange,
  rooms,
  groups,
  trainers,
  teamLeaders,
  clients,
  locations,
  statuses,
  conflicts,
  error,
  multiDayInfo,
  token
}) => {
  // Etat pour la fiche de chantier associee
  const [workSheet, setWorkSheet] = useState(null);
  const [loadingWorkSheet, setLoadingWorkSheet] = useState(false);
  const [showWorkSheetPreview, setShowWorkSheetPreview] = useState(false);
  const [workSheetPreviewUrl, setWorkSheetPreviewUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Verifier si une fiche de chantier existe pour ce numero de commande
  useEffect(() => {
    const checkWorkSheet = async () => {
      if (!formData.order_number || !token) {
        setWorkSheet(null);
        return;
      }

      setLoadingWorkSheet(true);
      try {
        const response = await fetch(
          API_ENDPOINTS.WORK_SHEETS_BY_ORDER(formData.order_number),
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        const data = await response.json();
        if (data.success && data.data && data.data.length > 0) {
          setWorkSheet(data.data[0]); // Prendre la plus recente
        } else {
          setWorkSheet(null);
        }
      } catch (err) {
        console.error('Erreur verification fiche de chantier:', err);
        setWorkSheet(null);
      } finally {
        setLoadingWorkSheet(false);
      }
    };

    // Debounce pour eviter trop de requetes
    const timeoutId = setTimeout(checkWorkSheet, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.order_number, token]);

  // Ouvrir la preview de la fiche de chantier
  const openWorkSheetPreview = async () => {
    if (!workSheet) return;

    setLoadingPreview(true);
    setShowWorkSheetPreview(true);

    try {
      const response = await fetch(
        API_ENDPOINTS.WORK_SHEET_DOWNLOAD(workSheet.id),
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Erreur lors du chargement');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      setWorkSheetPreviewUrl(url);
    } catch (err) {
      console.error('Erreur chargement fiche:', err);
      setShowWorkSheetPreview(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Fermer la preview
  const closeWorkSheetPreview = () => {
    if (workSheetPreviewUrl) {
      window.URL.revokeObjectURL(workSheetPreviewUrl);
    }
    setWorkSheetPreviewUrl(null);
    setShowWorkSheetPreview(false);
  };

  // Telecharger depuis la preview
  const downloadFromPreview = () => {
    if (!workSheetPreviewUrl || !workSheet) return;
    const link = document.createElement('a');
    link.href = workSheetPreviewUrl;
    link.download = workSheet.file_name;
    link.click();
  };

  // Nettoyer l'URL au demontage
  useEffect(() => {
    return () => {
      if (workSheetPreviewUrl) {
        window.URL.revokeObjectURL(workSheetPreviewUrl);
      }
    };
  }, [workSheetPreviewUrl]);
  // Utiliser les nouveaux noms avec fallback pour compatibilite
  const groupsList = groups || rooms || [];
  const teamLeadersList = teamLeaders || [];
  const clientsList = clients || [];
  const locationsList = locations || [];
  const prestationTypes = PRESTATION_TYPES || SESSION_TYPES;

  // Groupe sélectionné
  const selectedGroupId = parseInt(formData.group_id || formData.room_id) || null;

  // Convertir les listes en options pour react-select
  const clientOptions = clientsList.map(client => ({
    value: client.id,
    label: `${client.code} - ${client.name}`,
    code: client.code,
    name: client.name
  }));

  // Options pour les lieux de mise : Lieux classiques + Clients (chez client)
  // Groupées et triées par nom
  const locationOptionsFromLocations = locationsList.map(location => ({
    value: location.id,
    label: `${location.code} - ${location.name} (${location.city})`,
    sortName: location.name.toLowerCase(),
    type: 'location'
  }));

  // Ajouter les clients comme option "Chez [Client]" avec préfixe spécial
  // Exclure les partenaires et agences interim (garder uniquement les vrais clients)
  const locationOptionsFromClients = clientsList
    .filter(client => !client.client_type || client.client_type === 'client')
    .map(client => ({
      value: `client_${client.id}`, // Préfixe pour distinguer des locations
      label: `Chez ${client.name}`,
      sortName: client.name.toLowerCase(),
      type: 'client',
      clientId: client.id
    }));

  // Combiner et trier par nom
  const locationOptions = [
    ...locationOptionsFromLocations,
    ...locationOptionsFromClients
  ].sort((a, b) => a.sortName.localeCompare(b.sortName, 'fr'));

  // Filtrer les chefs d'équipe par groupe sélectionné
  const filteredTeamLeaders = selectedGroupId
    ? teamLeadersList.filter(leader =>
        leader.group_ids?.includes(selectedGroupId) || leader.group_id === selectedGroupId
      )
    : teamLeadersList;

  // Si aucun chef d'équipe dans le groupe, afficher tous (avec indication)
  const teamLeadersToShow = filteredTeamLeaders.length > 0 ? filteredTeamLeaders : teamLeadersList;
  const showAllTeamLeaders = filteredTeamLeaders.length === 0 && selectedGroupId;

  const teamLeaderOptions = teamLeadersToShow.map(leader => ({
    value: leader.id,
    label: showAllTeamLeaders
      ? `${leader.first_name} ${leader.last_name} (autre groupe)`
      : `${leader.first_name} ${leader.last_name}`,
    firstName: leader.first_name,
    lastName: leader.last_name
  }));

  const inputStyle = {
    width: '100%',
    padding: 'clamp(10px, 2.5vw, 14px)',
    border: '2px solid #e1e5e9',
    borderRadius: '10px',
    fontSize: 'clamp(14px, 2.5vw, 16px)',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    transition: 'all 0.2s ease',
    outline: 'none',
    minHeight: '44px'
  };

  const selectStyle = {
    ...inputStyle,
    appearance: 'none',
    backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'%23666\' viewBox=\'0 0 20 20\'%3e%3cpath d=\'M7 10l5 5 5-5H7z\'/%3e%3c/svg%3e")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    backgroundSize: '16px'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '6px',
    fontWeight: '600',
    color: '#495057',
    fontSize: 'clamp(13px, 2.5vw, 15px)'
  };

  const sectionTitleStyle = {
    margin: '0 0 16px 0',
    fontSize: 'clamp(16px, 3vw, 18px)',
    fontWeight: '600',
    color: '#495057',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    paddingBottom: '8px',
    borderBottom: '2px solid #e9ecef'
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(20px, 5vw, 32px)' }}>
      {/* Messages d'erreur et conflits */}
      {(error || conflicts.length > 0) && (
        <div>
          {error && (
            <div style={{
              background: 'linear-gradient(135deg, #f8d7da, #f1aeb5)',
              border: '2px solid #f5c6cb',
              color: '#721c24',
              padding: 'clamp(12px, 3vw, 16px)',
              borderRadius: '12px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: 'clamp(13px, 2.5vw, 15px)'
            }}>
              <span style={{ fontSize: '20px' }}>❌</span>
              <span>{error}</span>
            </div>
          )}

          {conflicts.length > 0 && (
            <div style={{
              background: 'linear-gradient(135deg, #fff3cd, #ffeaa7)',
              border: '2px solid #fbbf24',
              color: '#92400e',
              padding: 'clamp(12px, 3vw, 16px)',
              borderRadius: '12px',
              marginBottom: '16px',
              fontSize: 'clamp(13px, 2.5vw, 15px)'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px', 
                marginBottom: '8px',
                fontWeight: '600'
              }}>
                <span style={{ fontSize: '20px' }}>⚠️</span>
                <span>Conflits détectés :</span>
              </div>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {conflicts.map((conflict, index) => (
                  <li key={index} style={{ marginBottom: '4px' }}>{conflict}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Type de prestation - Uniquement en mode création */}
      {!formData.id && (
        <div>
          <h4 style={sectionTitleStyle}>
            <span style={{ fontSize: 'clamp(18px, 4vw, 20px)' }}>🎯</span>
            Type de prestation
          </h4>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'var(--session-type-grid, repeat(3, 1fr))',
            gap: 'clamp(8px, 2vw, 12px)'
          }}>
            {prestationTypes.map(type => (
              <label key={type.value} style={{
                display: 'flex',
                flexDirection: 'column',
                padding: 'clamp(12px, 3vw, 16px)',
                border: (formData.prestation_type || formData.session_type) === type.value ? '2px solid #667eea' : '2px solid #e2e8f0',
                borderRadius: '12px',
                cursor: 'pointer',
                background: (formData.prestation_type || formData.session_type) === type.value
                  ? 'linear-gradient(135deg, #f0f8ff, #e6f3ff)'
                  : 'white',
                transition: 'all 0.2s ease',
                minHeight: '44px',
                justifyContent: 'center'
              }}>
                <input
                  type="radio"
                  name="prestation_type"
                  value={type.value}
                  checked={(formData.prestation_type || formData.session_type) === type.value}
                  onChange={handleChange}
                  style={{ display: 'none' }}
                />
                <div style={{
                  fontSize: 'clamp(14px, 3vw, 16px)',
                  fontWeight: '600',
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>{type.icon}</span>
                  <span style={{ display: 'var(--session-type-text, inline)' }}>
                    {type.label}
                  </span>
                </div>
                <div style={{
                  fontSize: 'clamp(11px, 2vw, 12px)',
                  color: '#666',
                  display: 'var(--session-type-desc, block)'
                }}>
                  {type.description}
                </div>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Informations de base */}
      <div>
        <h4 style={sectionTitleStyle}>
          <span style={{ fontSize: 'clamp(18px, 4vw, 20px)' }}>📍</span>
          Groupe et responsables
        </h4>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'var(--form-grid-columns, 1fr 1fr)',
          gap: 'clamp(12px, 3vw, 20px)'
        }}>
          <div>
            <label style={labelStyle}>Groupe de travail *</label>
            <select
              name="group_id"
              value={formData.group_id || formData.room_id}
              onChange={handleChange}
              style={selectStyle}
              required
            >
              <option value="">Selectionner un groupe</option>
              {groupsList.map(group => (
                <option key={group.id} value={group.id}>
                  {group.code} - {group.name} ({group.city})
                </option>
              ))}
            </select>
          </div>

          {(formData.prestation_type || formData.session_type) === 'prestation' && (
            <>
              <div>
                <label style={labelStyle}>Client</label>
                <SearchableSelect
                  name="client_id"
                  value={formData.client_id || ''}
                  options={clientOptions}
                  onChange={handleChange}
                  placeholder="Rechercher un client..."
                  noOptionsMessage="Aucun client trouvé"
                />
              </div>

              <div>
                <label style={labelStyle}>Lieu de prestation</label>
                <SearchableSelect
                  name="location_id"
                  value={formData.location_id || ''}
                  options={locationOptions}
                  onChange={handleChange}
                  placeholder="Adresse du client (ou rechercher un lieu...)"
                  noOptionsMessage="Aucun lieu trouvé"
                />
              </div>

              <div>
                <label style={labelStyle}>Chef d'equipe *</label>
                <SearchableSelect
                  name="team_leader_id"
                  value={formData.team_leader_id || formData.trainer_id || ''}
                  options={teamLeaderOptions}
                  onChange={handleChange}
                  placeholder="Rechercher un chef d'équipe..."
                  noOptionsMessage="Aucun chef d'équipe trouvé"
                  isClearable={false}
                />
              </div>

              <div>
                <label style={labelStyle}>Statut *</label>
                <select
                  name="status_id"
                  value={formData.status_id || ''}
                  onChange={handleChange}
                  style={selectStyle}
                  required
                >
                  <option value="">Selectionner un statut</option>
                  {(statuses || []).map(status => (
                    <option key={status.id} value={status.id}>
                      {status.color === '#FFA500' ? '🟠' :
                       status.color === '#FFFF00' ? '🟡' :
                       status.color === '#008000' ? '🟢' :
                       status.color === '#0000FF' ? '🔵' : '⚪'} {status.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>N° de commande</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="text"
                    name="order_number"
                    value={formData.order_number || ''}
                    onChange={handleChange}
                    placeholder="Ex: 4647"
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  {formData.order_number && (
                    loadingWorkSheet ? (
                      <div style={{
                        width: '44px',
                        height: '44px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#6c757d'
                      }}>
                        ...
                      </div>
                    ) : workSheet ? (
                      <button
                        type="button"
                        onClick={openWorkSheetPreview}
                        title="Voir la fiche de chantier"
                        style={{
                          width: '44px',
                          height: '44px',
                          borderRadius: '10px',
                          border: 'none',
                          background: 'linear-gradient(135deg, #6f42c1, #5a32a3)',
                          color: 'white',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '18px',
                          flexShrink: 0,
                          transition: 'transform 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                      >
                        📄
                      </button>
                    ) : (
                      <div
                        title="Aucune fiche de chantier pour ce numero"
                        style={{
                          width: '44px',
                          height: '44px',
                          borderRadius: '10px',
                          border: '2px dashed #dee2e6',
                          background: '#f8f9fa',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '16px',
                          color: '#adb5bd',
                          flexShrink: 0
                        }}
                      >
                        📄
                      </div>
                    )
                  )}
                </div>
              </div>

              <div>
                <label style={labelStyle}>Objectif / Volumétrie</label>
                <input
                  type="text"
                  name="objective"
                  value={formData.objective || ''}
                  onChange={handleChange}
                  placeholder="Ex: 40 000 cols"
                  style={inputStyle}
                />
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Dates */}
      <div>
        <h4 style={sectionTitleStyle}>
          <span style={{ fontSize: 'clamp(18px, 4vw, 20px)' }}>📅</span>
          Planification
          {multiDayInfo && multiDayInfo.isMultiDay && (
            <span style={{
              fontSize: 'clamp(12px, 2vw, 13px)',
              fontWeight: '400',
              color: '#6c757d',
              marginLeft: '8px'
            }}>
              - Prestation {multiDayInfo.totalDays} jours
            </span>
          )}
        </h4>
        {(() => {
          const currentType = formData.prestation_type || formData.session_type;
          const isNote = currentType === 'note';
          const isPrestation = currentType === 'prestation';
          // 4 cols pour note (dates + heure début + heure fin), 3 pour prestation (dates + départ), 2 sinon
          const cols = isNote ? '1fr 1fr 1fr 1fr' : (isPrestation ? '1fr 1fr 1fr' : '1fr 1fr');
          return (
            <div style={{
              display: 'grid',
              gridTemplateColumns: `var(--date-grid-columns, ${cols})`,
              gap: 'clamp(12px, 3vw, 20px)'
            }}>
              <div>
                <label style={labelStyle}>Date de debut *</label>
                <input
                  type="date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleChange}
                  style={inputStyle}
                  required
                />
              </div>

              <div>
                <label style={labelStyle}>Date de fin *</label>
                <input
                  type="date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleChange}
                  style={inputStyle}
                  required
                />
              </div>

              {isPrestation && (
                <div>
                  <label style={labelStyle}>Heure de depart</label>
                  <input
                    type="time"
                    name="departure_time"
                    value={formData.departure_time}
                    onChange={handleChange}
                    style={inputStyle}
                  />
                </div>
              )}

              {isNote && (
                <>
                  <div>
                    <label style={labelStyle}>Heure de début</label>
                    <input
                      type="time"
                      name="departure_time"
                      value={formData.departure_time || ''}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Heure de fin</label>
                    <input
                      type="time"
                      name="end_time"
                      value={formData.end_time || ''}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {/* Option week-ends pour prestations traversant un WE */}
        {hasWeekendBetween(formData.start_date, formData.end_date) && (
          <div style={{
            marginTop: '16px',
            padding: '12px 16px',
            background: '#f8f9fa',
            borderRadius: '10px',
            border: '1px solid #e9ecef'
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
              fontSize: 'clamp(13px, 2.5vw, 15px)'
            }}>
              <input
                type="checkbox"
                name="include_weekends"
                checked={formData.include_weekends || false}
                onChange={handleChange}
                style={{ width: '18px', height: '18px' }}
              />
              <span>
                <strong>Inclure les week-ends</strong>
                <span style={{ color: '#6c757d', marginLeft: '8px' }}>
                  (samedi et dimanche travaillés)
                </span>
              </span>
            </label>
          </div>
        )}
      </div>

      {/* Champs specifiques selon le type */}
      {(formData.prestation_type || formData.session_type) === 'blocked' && (
        <div>
          <h4 style={sectionTitleStyle}>
            <span style={{ fontSize: 'clamp(18px, 4vw, 20px)' }}>🚫</span>
            Raison du blocage
          </h4>
          <input
            type="text"
            name="block_reason"
            value={formData.block_reason}
            onChange={handleChange}
            placeholder="Ex: Reserve pour client special, Reunion interne..."
            style={inputStyle}
            required
          />
        </div>
      )}

      {(formData.prestation_type || formData.session_type) === 'note' && (
        <div>
          <h4 style={sectionTitleStyle}>
            <span style={{ fontSize: 'clamp(18px, 4vw, 20px)' }}>📝</span>
            Description / Commentaire
          </h4>
          <textarea
            name="maintenance_description"
            value={formData.maintenance_description}
            onChange={handleChange}
            placeholder="Information ou maintenance..."
            style={{ ...inputStyle, height: '80px', resize: 'vertical' }}
          />
        </div>
      )}

      {/* Modal de previsualisation de la fiche de chantier (portail vers body) */}
      {showWorkSheetPreview && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 9999
          }}
          onClick={closeWorkSheetPreview}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 20px',
              background: 'linear-gradient(135deg, #6f42c1, #5a32a3)',
              borderBottom: '1px solid rgba(255,255,255,0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ color: 'white', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '24px' }}>📄</span>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                  Fiche de chantier
                </h3>
                <p style={{ margin: 0, fontSize: '13px', opacity: 0.8 }}>
                  {loadingPreview ? 'Chargement...' : `Commande ${formData.order_number}`}
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              {workSheetPreviewUrl && (
                <button
                  onClick={downloadFromPreview}
                  style={{
                    padding: '10px 20px',
                    background: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <span>⬇️</span> Telecharger
                </button>
              )}
              <button
                onClick={closeWorkSheetPreview}
                style={{
                  padding: '10px 20px',
                  background: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Fermer
              </button>
            </div>
          </div>

          {/* Contenu PDF */}
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {loadingPreview ? (
              <div style={{ textAlign: 'center', color: 'white' }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  border: '4px solid rgba(255,255,255,0.2)',
                  borderTopColor: 'white',
                  borderRadius: '50%',
                  margin: '0 auto 20px',
                  animation: 'spin 1s linear infinite'
                }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                <p style={{ fontSize: '16px' }}>Chargement de la fiche...</p>
              </div>
            ) : workSheetPreviewUrl ? (
              <iframe
                src={workSheetPreviewUrl}
                title="Fiche de chantier"
                style={{
                  width: '100%',
                  maxWidth: '900px',
                  height: 'calc(100vh - 120px)',
                  border: 'none',
                  borderRadius: '8px',
                  background: 'white',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
                }}
              />
            ) : (
              <div style={{ textAlign: 'center', color: 'white' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
                <p>Erreur lors du chargement de la fiche</p>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
});

// Nom d'affichage pour les DevTools React
BasicInfoTab.displayName = 'BasicInfoTab';

export default BasicInfoTab;